import { useEffect, useState, useCallback } from "react";
import { Plus, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { CaptionedImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["general", "safety", "quality", "vendor", "tooling"];
const EMPTY = { subject: "", message: "", category: "general", rating: 5, image_id: null, image_caption: "" };

export default function Feedback() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/feedback");
    setRows(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/feedback", { ...form, rating: Number(form.rating) });
      toast.success("Feedback submitted");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}/status`, { status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="feedback-page">
      <PageHeader
        overline="Voice of the floor"
        title="Feedback"
        description="Raise issues about safety, quality, vendors or tooling — and see how they're handled."
        action={
          <Button data-testid="add-feedback-button" onClick={() => setOpen(true)} className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
            <Plus size={16} className="mr-2" /> Give feedback
          </Button>
        }
      />

      {rows.length === 0 ? (
        <EmptyState testid="feedback-empty" title="No feedback yet" description="Be the first to share what's working and what isn't." />
      ) : (
        <div className="divide-y divide-border border border-border bg-white" data-testid="feedback-list">
          {rows.map((f) => (
            <div key={f.id} data-testid={`feedback-item-${f.id}`} className="p-4 transition-colors duration-150 hover:bg-slate-50">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-base font-semibold tracking-tight text-[#0F172A]">{f.subject}</span>
                    <StatusBadge value={f.category} />
                    <StatusBadge testid={`feedback-status-${f.id}`} value={f.status} />
                  </div>
                  <p className="mt-2 max-w-3xl text-sm text-slate-600">{f.message}</p>
                  <CaptionedImage
                    testid={`feedback-image-${f.id}`}
                    imageId={f.image_id}
                    caption={f.image_caption}
                    className="max-h-64 w-full max-w-sm border border-border object-cover"
                  />
                  <div className="mt-2 text-xs text-slate-400">
                    {f.author_name} · {(f.created_at || "").slice(0, 10)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div className="flex items-center gap-1 font-mono text-xs text-slate-500">
                    <Star size={13} weight="fill" className="text-[#FFCC00]" /> {f.rating}/5
                  </div>
                  {user?.role === "admin" && (
                    <Select value={f.status} onValueChange={(v) => setStatus(f.id, v)}>
                      <SelectTrigger data-testid={`feedback-status-select-${f.id}`} className="h-8 w-28 rounded-none border-slate-300 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["open", "reviewed", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Share feedback</DialogTitle>
            <DialogDescription>Tell us what is working on the floor and what is not.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-feedback-form">
            <div>
              <Label className="overline text-slate-500">Subject</Label>
              <Input data-testid="feedback-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="feedback-category-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Rating (1-5)</Label>
                <Input data-testid="feedback-rating-input" type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Message</Label>
              <Textarea data-testid="feedback-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={4} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <ImageCaptionField
              testid="feedback-image"
              value={form.image_id}
              caption={form.image_caption}
              onChange={({ image_id, image_caption }) =>
                setForm((f) => ({ ...f, image_id, image_caption }))
              }
            />
            <DialogFooter>
              <Button data-testid="save-feedback-button" type="submit" className="rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
                Submit feedback
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
